import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';

import { canAddNewPage, showAddPageForm } from 'components/AddPage/utils';

import {
  signOutRequest,
  seenNotification,
  clearAllNotifications,
  sendFeedback,
  notificationsOpened,
  toggleNotificationSound,
} from 'modules/Users/store/actions';
import {
  toggleAddProjectModal,
  toggleShareProjectModal,
  deleteProject as deleteProjectAction,
} from 'modules/Projects/store/actions';
import {
  contactUpdate,
  toggleSubmitFeedback,
  inspectExitCallback,
  toggleKeyboardShortcuts,
} from 'modules/App/store/actions';
import {
  addWebPage,
  addImage,
  findSimilarPages,
  updateAllPageVersions,
} from 'modules/Web/store/actions';
import { toggleTeamAddModal } from 'modules/Team/store/actions';

import useIntegrations from 'utils/useIntegrations';
import { nullFunction } from 'utils';

import Header from '../components/Header';

const HeaderContainer = ({
  match,
  dispatch,
  projects,
  pages,
  currentPage,
  user,
  notifications,
  currentProject,
  feedback,
  showSubmitFeedback,
  inspectChangesCount,
  teams,
  subscriptions,
  showKeyboardShortcuts,
  billing,
  extension,
}) => {
  const [feedbackSending, setFeedbackSending] = useState(false);
  const [showWhatsNew, setShowWhatsNew] = useState(false);
  // const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);
  // const [showTeamDropdown, setTeamDropdown] = useState(false);

  const {
    checkIsAppIntegrated,
    onIntegrateSlack,
    onIntegrateTrello,
    onIntegrateAsana,
  } = useIntegrations();

  const navigate = useNavigate();
  const location = useLocation();

  /* eslint-disable indent */
  const sortedPageVersions = () =>
    currentPage.pageVersions && currentPage.pageVersions.length > 0
      ? currentPage.pageVersions.sort(
          (a, b) => b.createdAt.valueOf() - a.createdAt.valueOf(),
        )
      : [];
  /* eslint-enable indent */

  const signOutDispatch = () => {
    const fn = () => dispatch(signOutRequest({}, navigate));

    if (inspectChangesCount > 0) setExitConfirmCallback({ fn, args: [] });
    else fn();
  };

  const seenNotificationDispatch = (payload) => {
    const fn = (id) => dispatch(seenNotification(id));

    if (inspectChangesCount > 0)
      setExitConfirmCallback({ fn, args: [payload] });
    else fn(payload);
  };

  const addWebPageDispatch = (payload) => {
    dispatch(addWebPage(payload, nullFunction, navigate));

    if (payload.addSimilar) {
      dispatch(findSimilarPages(payload));
    }
  };

  const helpSubmit = (payload) => {
    dispatch(
      sendFeedback({
        ...payload,
        userEmail: user.email,
        userID: user.uid,
        url: `${window.location.origin}${location.pathname}${location.search}`,
      }),
    );
    setFeedbackSending(true);
  };

  const showContactModal = () => {
    dispatch(
      contactUpdate({
        showContactModal: true,
        success: false,
      }),
    );
  };

  const linkClickHandler = (e) => {
    e.preventDefault();

    let { href } = e.target.closest('a');
    const fn = (to) => navigate(to);

    href = new URL(href);
    href = `${href.pathname}${href.hash}${href.search}`;

    if (inspectChangesCount > 0) setExitConfirmCallback({ fn, args: [href] });
    else fn(href);
  };

  const setExitConfirmCallback = (payload) => {
    dispatch(inspectExitCallback(payload));
  };

  /* const toggleTeamDropdown = useCallback(() => {
    setTeamDropdown((state) => !state);
  }, []); */

  const getOwnedTeam = () => {
    if (teams && teams.length) {
      return teams?.find((t) => t.owner === user.uid) || teams[0];
    }

    return null;
  };

  const currentProjectAccess = () => {
    if (!location.pathname.startsWith('/project/')) return null;

    if (currentProject.owner === user.uid)
      return { text: 'Owner', id: 'owner' };

    if (currentProject.users?.length) {
      const foundUser = currentProject.users.find((i) => i.uid === user.uid);

      if (foundUser && foundUser.access)
        return {
          id: foundUser.access,
          text: `${foundUser.access
            .substr(0, 1)
            .toUpperCase()}${foundUser.access.substr(1)} access`,
        };
    }

    return null;
  };

  const deleteProject = () => {
    if (currentProject.id) {
      dispatch(deleteProjectAction({ id: currentProject.id }));
      navigate('/projects');
    }
  };

  useEffect(() => {
    if (feedback.sent) setFeedbackSending(false);
  }, [feedback]);

  return (
    <Header
      match={match}
      pathname={location.pathname}
      signOut={signOutDispatch}
      showAddProjectModal={() => {
        dispatch(toggleAddProjectModal(true));
      }}
      showShareProjectModal={() => {
        dispatch(toggleShareProjectModal(true));
      }}
      users={currentProject.users}
      notifications={notifications}
      pageVersions={sortedPageVersions()}
      changePageVersionTo={(payload) => {
        navigate(
          `/project/${match.params.projectID}/${match.params.pageID}/${payload}/${match.params.currentView}`,
        );
      }}
      user={user}
      showAddPageForm={
        /* eslint-disable indent */
        currentProject?.type === 'demo'
          ? pages.length < 5
          : showAddPageForm(
              user?.uid || '',
              currentProject?.owner || '',
              currentProject?.users || [],
              Array.isArray(teams) ? teams[0] : null,
            ) && pages.length > 0
        /* eslint-enable indent */
      }
      canAddNewPage={canAddNewPage(
        currentProject?.owner || '',
        currentProject?.users || [],
        pages?.length || 0,
      )}
      seenNotification={seenNotificationDispatch}
      currentProjectTitle={currentProject.title}
      addWebPage={addWebPageDispatch}
      isCurrentProjectArchived={currentProject.isArchived}
      currentProjectType={currentProject.type}
      clearAllNotifications={() => {
        dispatch(clearAllNotifications());
      }}
      helpSubmit={helpSubmit}
      feedbackSending={feedbackSending}
      feedbackSent={feedback.sent}
      showContactModal={showContactModal}
      setSubmitFeedbackDropDown={(state) => {
        dispatch(toggleSubmitFeedback(state));
      }}
      showSubmitFeedback={showSubmitFeedback}
      linkClickHandler={linkClickHandler}
      currentProjectArchivedPagesLength={
        (pages && pages.filter((i) => i.isArchived === true).length) || 0
      }
      currentArchivedPages={
        (pages && pages.filter((i) => i.isArchived === true)) || []
      }
      pages={pages || []}
      projects={projects}
      notificationsOpenedOnce={() => {
        dispatch(notificationsOpened());
      }}
      doesTeamExist={
        (teams &&
          teams.length > 0 &&
          teams.some(
            ({ id, owner: teamOwner, users: teamUsers, isDisabled }) =>
              id === match.params.teamID &&
              isDisabled !== true &&
              (teamOwner === user.uid ||
                teamUsers.some(
                  (u) => u.uid === user.uid && u.access === 'manager',
                )),
          )) ||
        false
      }
      toggleTeamAddModal={() => {
        dispatch(toggleTeamAddModal());
      }}
      showWhatsNew={showWhatsNew}
      setShowWhatsNew={setShowWhatsNew}
      showKeyboardShortcuts={showKeyboardShortcuts}
      setShowKeyboardShortcuts={(payload) => {
        dispatch(toggleKeyboardShortcuts(payload));
      }}
      subscriptions={subscriptions}
      onIntegrateSlack={onIntegrateSlack}
      onIntegrateTrello={onIntegrateTrello}
      checkIsAppIntegrated={checkIsAppIntegrated}
      // toggleTeamDropdown={toggleTeamDropdown}
      // showTeamDropdown={showTeamDropdown}
      // teams={teams || []}
      addImage={(payload) => {
        dispatch(addImage(payload, nullFunction, navigate));
      }}
      team={getOwnedTeam()}
      toggleNotificationSoundDispatch={() => {
        dispatch(toggleNotificationSound());
      }}
      onIntegrateAsana={onIntegrateAsana}
      currentProjectAccess={currentProjectAccess()}
      updateAllPagesToLatestVersion={() => {
        dispatch(updateAllPageVersions());
      }}
      billing={billing}
      extension={extension}
      deleteProject={deleteProject}
    />
  );
};

const mapStateToProps = ({
  app: {
    showSubmitFeedback,
    inspectChangesCount,
    showKeyboardShortcuts,
    extension,
  },
  user: { user, notifications, feedback, subscriptions, billing },
  web: { pages, currentPage },
  project: {
    projects,
    currentProject,
    // currentProject: {
    //   title: currentProjectTitle,
    //   users,
    //   owner,
    //   isArchived: isCurrentProjectArchived,
    //   type: currentProjectType,
    // },
  },
  team: { teams },
}) => ({
  showSubmitFeedback,
  user,
  notifications,
  projects,
  pages,
  currentPage,
  currentProject,
  feedback,
  inspectChangesCount,
  subscriptions,
  teams,
  showKeyboardShortcuts,
  billing,
  extension,
});

const mapDispatchToProps = (dispatch) => ({ dispatch });

HeaderContainer.propTypes = {
  match: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  projects: PropTypes.array,
  dispatch: PropTypes.func.isRequired,
  currentProject: PropTypes.object,
  pages: PropTypes.array.isRequired,
  currentPage: PropTypes.object.isRequired,
  notifications: PropTypes.object.isRequired,
  showSubmitFeedback: PropTypes.bool.isRequired,
  feedback: PropTypes.object.isRequired,
  inspectChangesCount: PropTypes.number.isRequired,
  subscriptions: PropTypes.array.isRequired,
  teams: PropTypes.array,
  showKeyboardShortcuts: PropTypes.bool.isRequired,
  billing: PropTypes.object,
  extension: PropTypes.shape({
    installed: PropTypes.bool.isRequired,
    accessible: PropTypes.bool.isRequired,
  }).isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(HeaderContainer);
